import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Check, ChevronLeft, ChevronRight, Save, Clock, Building2, BedDouble, Layers, Globe2, Sparkles, SkipForward, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Step1General from "./steps/Step1General";
import Step2Property from "./steps/Step2Property";
import Step3RoomTypes from "./steps/Step3RoomTypes";
import Step4Channels from "./steps/Step4Channels";
import Step5BookingEngine from "./steps/Step5BookingEngine";

// 5-step wizard — a fresh hotel can go from empty to accepting bookings in under 10 minutes.
// Steps 6 (Payments), 7 (Staff) and 8 (Automation) are configured post-launch in Settings
// with sensible defaults applied automatically on `complete_draft` backend call.
const STEPS = [
    {
        key: "step1",
        titleKey: "wizard.step_property.title",
        subtitleKey: "wizard.step_property.subtitle",
        titleFallback: "Prona",
        subtitleFallback: "Emri, adresa dhe kontaktet",
        icon: Building2,
        Component: Step1General,
        canSkip: false,
        requiredFields: ["hotel_name", "contact_email"],
    },
    {
        key: "step2",
        titleKey: "wizard.step_rooms.title",
        subtitleKey: "wizard.step_rooms.subtitle",
        titleFallback: "Dhomat",
        subtitleFallback: "Numri, katet dhe politikat",
        icon: BedDouble,
        Component: Step2Property,
        canSkip: false,
        requiredFields: ["property_name", "num_rooms"],
    },
    {
        key: "step3",
        titleKey: "wizard.step_room_types.title",
        subtitleKey: "wizard.step_room_types.subtitle",
        titleFallback: "Llojet & Çmimet",
        subtitleFallback: "Kategoritë e dhomave dhe tarifat",
        icon: Layers,
        Component: Step3RoomTypes,
        canSkip: false,
        requiredFields: [],
    },
    {
        key: "step4",
        titleKey: "wizard.step_channels.title",
        subtitleKey: "wizard.step_channels.subtitle",
        titleFallback: "OTA / Channex",
        subtitleFallback: "Distribuim opsional",
        icon: Globe2,
        Component: Step4Channels,
        canSkip: true,
        requiredFields: [],
    },
    {
        key: "step5",
        titleKey: "wizard.step_engine.title",
        subtitleKey: "wizard.step_engine.subtitle",
        titleFallback: "Booking Engine",
        subtitleFallback: "Aktivizim dhe URL publike",
        icon: Sparkles,
        Component: Step5BookingEngine,
        canSkip: true,
        requiredFields: [],
    },
];

export default function HotelWizard() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [draft, setDraft] = useState(null);
    const [stepIdx, setStepIdx] = useState(0);
    const [saving, setSaving] = useState(false);
    const [completing, setCompleting] = useState(false);
    const dirtyRef = useRef(false);

    // i18n helper with fallback so wizard renders even before Shqip keys are added.
    const tr = useCallback((key, fallback) => {
        const v = t(key);
        return v === key ? fallback : v;
    }, [t]);

    useEffect(() => {
        api.get(`/onboarding/drafts/${id}`).then(({ data }) => {
            setDraft(data);
            // Clamp saved step to available 5 steps (older drafts may have current_step 6-8).
            const clamped = Math.min(Math.max(0, (data.current_step || 1) - 1), STEPS.length - 1);
            setStepIdx(clamped);
        }).catch(() => { toast.error(tr("wizard.draft_not_found", "Drafti nuk u gjet")); navigate("/wizard"); });
    }, [id, navigate, tr]);

    const updateStep = useCallback((stepKey, patch) => {
        setDraft((d) => ({ ...d, data: { ...d.data, [stepKey]: { ...d.data[stepKey], ...patch } } }));
        dirtyRef.current = true;
    }, []);

    const save = useCallback(async (silent = false) => {
        if (!draft) return;
        setSaving(true);
        try {
            const completed = Array.from(new Set([...(draft.completed_steps || []), STEPS[stepIdx].key]));
            const { data } = await api.put(`/onboarding/drafts/${draft.id}`, {
                data: draft.data,
                current_step: stepIdx + 1,
                completed_steps: completed,
            });
            setDraft(data);
            dirtyRef.current = false;
            if (!silent) toast.success(tr("wizard.saved", "U ruajt"));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr("wizard.save_failed", "Ruajtja dështoi")); }
        finally { setSaving(false); }
    }, [draft, stepIdx, tr]);

    const goto = async (idx) => {
        if (dirtyRef.current) await save(true);
        setStepIdx(idx);
    };

    const validateCurrentStep = () => {
        const step = STEPS[stepIdx];
        const data = draft.data[step.key] || {};
        const missing = step.requiredFields.filter((f) => {
            const v = data[f];
            return v === undefined || v === null || v === "" || (typeof v === "number" && v <= 0);
        });
        if (missing.length) {
            toast.error(tr("wizard.missing_fields", "Fusha të detyrueshme") + ": " + missing.join(", "));
            return false;
        }
        return true;
    };

    const next = async () => {
        if (!validateCurrentStep()) return;
        await save(true);
        setStepIdx(stepIdx + 1);
    };

    const skip = async () => {
        // Mark as visited but not populate — advance to next step.
        await save(true);
        setStepIdx(stepIdx + 1);
    };

    const [publishResult, setPublishResult] = useState(null);

    const complete = async () => {
        if (!validateCurrentStep()) return;
        await save(true);
        setCompleting(true);
        try {
            const { data } = await api.post(`/onboarding/drafts/${draft.id}/complete`);
            // Immediately publish so the hotel is bookable from the widget URL.
            let pub = null;
            try {
                const { data: p } = await api.post(`/onboarding/drafts/${draft.id}/publish`);
                pub = p;
            } catch (_) { /* publish is optional — user can toggle later in settings */ }
            toast.success(tr("wizard.hotel_ready", "Hoteli është gati") + `! ${data.rooms_created} ` + tr("wizard.rooms", "dhoma"));
            setPublishResult({ ...data, ...(pub || {}) });
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr("wizard.completion_failed", "Përfundimi dështoi")); }
        finally { setCompleting(false); }
    };

    if (!draft) {
        return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{tr("wizard.loading", "Duke ngarkuar draftin…")}</div>;
    }

    if (publishResult) {
        return <PublishSuccess result={publishResult} draft={draft} onDone={() => navigate("/dashboard")} tr={tr} />;
    }

    const step = STEPS[stepIdx];
    const StepComp = step.Component;
    const stepData = draft.data[step.key];
    const progressPct = Math.round(((stepIdx + 1) / STEPS.length) * 100);
    const isLast = stepIdx === STEPS.length - 1;

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col bg-background" data-testid="hotel-wizard">
            {/* Header */}
            <div className="flex h-14 items-center gap-3 border-b border-border bg-card px-4">
                <button
                    onClick={() => navigate("/wizard")}
                    className="rounded-md p-1 text-muted-foreground hover:bg-muted/60"
                    title={tr("wizard.back_to_drafts", "Kthehu te draftet")}
                    data-testid="wizard-close"
                >
                    <ChevronLeft size={16} />
                </button>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold tracking-tight">
                        {draft.data.step1.hotel_name || tr("wizard.new_hotel", "Hotel i ri")} — {tr("wizard.setup", "Konfigurimi")}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{tr("wizard.step", "Hapi")} {stepIdx + 1}/{STEPS.length}</span>
                        <span aria-hidden>·</span>
                        <Clock size={10} />
                        <span>{tr("wizard.under_10_min", "Gati për rezervime nën 10 minuta")}</span>
                    </div>
                </div>
                <button onClick={() => save(false)} disabled={saving} className="akm-btn-ghost" data-testid="wizard-save">
                    <Save size={14} /> {saving ? tr("wizard.saving", "Duke ruajtur…") : tr("wizard.save_exit", "Ruaj & dil")}
                </button>
            </div>

            {/* Progress bar */}
            <div className="h-1 w-full bg-muted/40" data-testid="wizard-progress-track">
                <div
                    className="h-full bg-[#ff7b02] transition-all duration-300 ease-out"
                    style={{ width: `${progressPct}%` }}
                    data-testid="wizard-progress-fill"
                />
            </div>

            <div className="flex min-h-0 flex-1">
                {/* Stepper sidebar */}
                <aside className="w-[260px] shrink-0 overflow-y-auto border-r border-border bg-card p-3">
                    <ol className="space-y-1">
                        {STEPS.map((s, i) => {
                            const isActive = i === stepIdx;
                            const isDone = draft.completed_steps?.includes(s.key);
                            const Icon = s.icon;
                            return (
                                <li key={s.key}>
                                    <button
                                        onClick={() => goto(i)}
                                        className={`group flex w-full items-start gap-3 rounded-md px-2.5 py-2 text-left transition-colors ${
                                            isActive ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-muted/60"
                                        }`}
                                        data-testid={`wizard-step-${i + 1}`}
                                    >
                                        <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                                            isDone ? "bg-[#ff7b02] text-white" : isActive ? "border border-[#ff7b02] text-[#ff7b02]" : "border border-border text-muted-foreground"
                                        }`}>
                                            {isDone ? <Check size={11} /> : i + 1}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5">
                                                <Icon size={12} className={isActive ? "text-[#ff7b02]" : "text-muted-foreground"} />
                                                <div className="text-[13px] font-medium">{tr(s.titleKey, s.titleFallback)}</div>
                                                {s.canSkip && <span className="ml-auto text-[9px] uppercase tracking-wider text-muted-foreground">{tr("wizard.optional", "opsional")}</span>}
                                            </div>
                                            <div className="mt-0.5 text-[11px] text-muted-foreground">{tr(s.subtitleKey, s.subtitleFallback)}</div>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ol>

                    <div className="mt-4 rounded-md border border-border bg-muted/30 p-3 text-[11px] text-muted-foreground" data-testid="wizard-autosave-hint">
                        {tr("wizard.autosave_hint", "Progresi ruhet automatikisht. Mund të largohesh dhe të vazhdosh më vonë.")}
                    </div>
                </aside>

                {/* Step content */}
                <div className="flex min-w-0 flex-1 flex-col">
                    <div className="min-h-0 flex-1 overflow-y-auto p-6">
                        <div className="mx-auto max-w-3xl">
                            <div className="mb-5">
                                <h2 className="text-xl font-semibold tracking-tight" data-testid="wizard-step-title">{tr(step.titleKey, step.titleFallback)}</h2>
                                <p className="mt-1 text-sm text-muted-foreground">{tr(step.subtitleKey, step.subtitleFallback)}</p>
                            </div>
                            <StepComp data={stepData} update={(patch) => updateStep(step.key, patch)} draft={draft} />
                        </div>
                    </div>

                    {/* Footer actions */}
                    <div className="flex items-center gap-2 border-t border-border bg-card px-6 py-3">
                        <button
                            disabled={stepIdx === 0}
                            onClick={() => goto(stepIdx - 1)}
                            className="akm-btn-ghost disabled:opacity-50"
                            data-testid="wizard-back"
                        ><ChevronLeft size={14} /> {tr("wizard.back", "Mbrapa")}</button>
                        <div className="flex-1" />
                        <div className="text-[11px] text-muted-foreground">
                            {saving ? tr("wizard.saving", "Duke ruajtur…") : dirtyRef.current ? tr("wizard.unsaved", "Ndryshime të paruajtura") : tr("wizard.all_saved", "Ruajtur")}
                        </div>
                        {step.canSkip && !isLast && (
                            <button
                                onClick={skip}
                                className="akm-btn-ghost"
                                data-testid="wizard-skip"
                                title={tr("wizard.skip_hint", "Mund të konfigurohet më vonë nga Cilësimet")}
                            >
                                <SkipForward size={14} /> {tr("wizard.skip", "Kapërce për tani")}
                            </button>
                        )}
                        {!isLast ? (
                            <button
                                onClick={next}
                                className="akm-btn-primary"
                                data-testid="wizard-next"
                            >{tr("wizard.continue", "Vazhdo")} <ChevronRight size={14} /></button>
                        ) : (
                            <button
                                onClick={complete}
                                disabled={completing}
                                className="akm-btn-primary"
                                data-testid="wizard-finish"
                            >{completing ? tr("wizard.finalizing", "Duke finalizuar…") : tr("wizard.publish", "Publiko dhe fillo rezervimet")} <Check size={14} /></button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Publish success screen ────────────────────────────────────────────────
function PublishSuccess({ result, draft, onDone, tr }) {
    const [copied, setCopied] = useState(false);
    const bookingPath = result.booking_url || `/be/${draft.data?.step5?.subdomain || draft.hotel_id}`;
    const bookingURL = typeof window !== "undefined" ? `${window.location.origin}${bookingPath}` : bookingPath;
    const embedSnippet = result.widget_snippet || `<script src="/api/booking-engine/widget.js?hotel=${draft.hotel_id}" async></script>`;

    const copy = async (val) => {
        try {
            await navigator.clipboard.writeText(val);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch (_) { /* ignore */ }
    };

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center bg-background px-4" data-testid="publish-success">
            <div className="w-full max-w-2xl">
                <div className="mb-6 text-center">
                    <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                        <Check size={28} />
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight text-foreground">
                        {tr("wizard.published_title", "Urime, hoteli juaj është online!")}
                    </h1>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                        {tr("wizard.published_subtitle", "Booking Engine u aktivizua. Ndani lidhjen ose ngjisni widget-in në faqen tuaj.")}
                    </p>
                </div>

                <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                    <div className="mb-4">
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {tr("wizard.booking_link", "Lidhja e rezervimeve")}
                        </div>
                        <div className="flex items-center gap-2">
                            <input readOnly value={bookingURL} className="akm-input flex-1 font-mono text-[12px]" data-testid="publish-booking-url" />
                            <button onClick={() => copy(bookingURL)} className="akm-btn-ghost" data-testid="copy-booking-url">
                                {copied ? tr("wizard.copied", "Kopjuar") : tr("wizard.copy", "Kopjo")}
                            </button>
                        </div>
                    </div>

                    <div>
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {tr("wizard.embed_snippet", "Widget për faqen tuaj")}
                        </div>
                        <textarea readOnly value={embedSnippet} rows={2} className="akm-input w-full font-mono text-[11.5px]" data-testid="publish-widget-snippet" />
                        <button onClick={() => copy(embedSnippet)} className="mt-1 text-[11.5px] text-primary hover:underline">
                            {tr("wizard.copy_snippet", "Kopjo snippet")}
                        </button>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                    <Stat label={tr("wizard.rooms", "Dhoma")} value={result.rooms_created || 0} />
                    <Stat label={tr("wizard.users_created", "Staf")} value={(result.users_created || 0) + 1} />
                    <Stat label={tr("wizard.status", "Statusi")} value={result.is_published ? "LIVE" : "Draft"} highlight={result.is_published} />
                </div>

                <div className="mt-6 flex items-center justify-center gap-2">
                    <button onClick={onDone} className="akm-btn-primary" data-testid="publish-goto-dashboard">
                        {tr("wizard.goto_dashboard", "Shko te Dashboard")} <ArrowRight size={14} />
                    </button>
                    <a
                        href={bookingPath}
                        target="_blank"
                        rel="noreferrer"
                        className="akm-btn-ghost"
                        data-testid="publish-preview"
                    >
                        {tr("wizard.preview_engine", "Shiko Booking Engine")}
                    </a>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div className="rounded-lg border border-border bg-card p-3">
            <div className={`text-lg font-semibold tabular-nums ${highlight ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"}`}>{value}</div>
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
        </div>
    );
}
